import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { measurementsAPI, customersAPI } from '../services/api';
import { FiArrowLeft } from 'react-icons/fi';
import { useAlert } from '../context/AlertContext';

const AddMeasurement = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const customerIdFromUrl = searchParams.get('customer_id');
  
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const alert = useAlert();
  const [formData, setFormData] = useState({
    customer_id: customerIdFromUrl || '',
    garment_type: 'Shirt',
    chest: '',
    waist: '',
    hips: '',
    shoulder_width: '',
    sleeve_length: '',
    shirt_length: '',
    pant_length: '',
    inseam: '',
    outseam: '',
    neck_size: '',
    bicep: '',
    forearm: '',
    thigh: '',
    knee: '',
    ankle: '',
    notes: '',
    measurement_date: new Date().toISOString().split('T')[0],
  });

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await customersAPI.getAll();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
      alert.push({ type: 'error', title: 'Load failed', message: 'Could not load customers list.' });
    }
  }, [alert]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customer_id || !formData.garment_type) {
      alert.push({ type: 'warning', title: 'Missing data', message: 'Customer and garment type are required' });
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        ...formData,
        measurement_date: formData.measurement_date ? new Date(formData.measurement_date).toISOString() : new Date().toISOString(),
      };
      await measurementsAPI.create(submitData);
      alert.push({ type: 'success', title: 'Measurement saved' });
      navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/measurements');
    } catch (error) {
      console.error('Error creating measurement:', error);
      alert.push({ type: 'error', title: 'Create failed', message: 'Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const garmentTypes = ['Shirt', 'Pant', 'Suit', 'Blazer', 'Kurta', 'Sherwani', 'Dress', 'Other'];

  return (
    <div className="max-w-4xl mx-auto">
      <button
        onClick={() => navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/measurements')}
        className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 mb-6"
      >
        <FiArrowLeft size={20} />
        <span>Back</span>
      </button>

      <div className="bg-white rounded-lg shadow-md p-6">
        <h1 className="text-3xl font-bold text-gray-800 mb-6">Add New Measurement</h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Customer <span className="text-red-500">*</span>
              </label>
              <select
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                required
                disabled={!!customerIdFromUrl}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
              >
                <option value="">Select Customer</option>
                {customers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Garment Type <span className="text-red-500">*</span>
              </label>
              <select
                name="garment_type"
                value={formData.garment_type}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {garmentTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Measurement Date
              </label>
              <input
                type="date"
                name="measurement_date"
                value={formData.measurement_date}
                onChange={handleChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Body Measurements (in inches)</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Chest</label>
                <input
                  type="number"
                  step="0.1"
                  name="chest"
                  value={formData.chest}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Waist</label>
                <input
                  type="number"
                  step="0.1"
                  name="waist"
                  value={formData.waist}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Hips</label>
                <input
                  type="number"
                  step="0.1"
                  name="hips"
                  value={formData.hips}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shoulder Width</label>
                <input
                  type="number"
                  step="0.1"
                  name="shoulder_width"
                  value={formData.shoulder_width}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Neck Size</label>
                <input
                  type="number"
                  step="0.1"
                  name="neck_size"
                  value={formData.neck_size}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Bicep</label>
                <input
                  type="number"
                  step="0.1"
                  name="bicep"
                  value={formData.bicep}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Forearm</label>
                <input
                  type="number"
                  step="0.1"
                  name="forearm"
                  value={formData.forearm}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Shirt Measurements</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sleeve Length</label>
                <input
                  type="number"
                  step="0.1"
                  name="sleeve_length"
                  value={formData.sleeve_length}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shirt Length</label>
                <input
                  type="number"
                  step="0.1"
                  name="shirt_length"
                  value={formData.shirt_length}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Pant Measurements</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pant Length</label>
                <input
                  type="number"
                  step="0.1"
                  name="pant_length"
                  value={formData.pant_length}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Inseam</label>
                <input
                  type="number"
                  step="0.1"
                  name="inseam"
                  value={formData.inseam}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Outseam</label>
                <input
                  type="number"
                  step="0.1"
                  name="outseam"
                  value={formData.outseam}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Thigh</label>
                <input
                  type="number"
                  step="0.1"
                  name="thigh"
                  value={formData.thigh}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Knee</label>
                <input
                  type="number"
                  step="0.1"
                  name="knee"
                  value={formData.knee}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ankle</label>
                <input
                  type="number"
                  step="0.1"
                  name="ankle"
                  value={formData.ankle}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={3}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="Any additional notes about the measurements"
            />
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-600 text-white py-3 px-6 rounded-lg hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
            >
              {loading ? 'Saving...' : 'Save Measurement'}
            </button>
            <button
              type="button"
              onClick={() => navigate(customerIdFromUrl ? `/customers/${customerIdFromUrl}` : '/measurements')}
              className="px-6 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddMeasurement;

